import React, { useState, useEffect } from 'react';
import '../styles/MergeSortVisualizer.css'; // Import specific CSS

function MergeSortVisualizer({ array }) {
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [mode, setMode] = useState('manual');
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    if (array.length > 0) {
      mergeSort(array);
    }
  }, [array]);

  useEffect(() => {
    if (mode === 'automatic') {
      const id = setInterval(() => {
        setCurrentStep((prevStep) => {
          if (prevStep < steps.length - 1) {
            return prevStep + 1;
          } else {
            clearInterval(id);
            return prevStep;
          }
        });
      }, 2000); // 2 seconds
      setIntervalId(id);
      return () => clearInterval(id);
    }
  }, [mode, steps]);

  const mergeSort = (arr) => {
    const stepsArray = [];
    const merge = (left, right) => {
      let result = [];
      let leftIndex = 0;
      let rightIndex = 0;

      while (leftIndex < left.length && rightIndex < right.length) {
        if (left[leftIndex] < right[rightIndex]) {
          result.push(left[leftIndex]);
          leftIndex++;
        } else {
          result.push(right[rightIndex]);
          rightIndex++;
        }
      }

      return result.concat(left.slice(leftIndex)).concat(right.slice(rightIndex));
    };

    const mergeSortRec = (array, depth = 0) => {
      if (array.length <= 1) {
        return array;
      }

      const mid = Math.floor(array.length / 2);
      const left = array.slice(0, mid);
      const right = array.slice(mid);

      stepsArray.push({ 
        left, 
        right, 
        merged: [], 
        description: `Dividing array into left [${left.join(', ')}] and right [${right.join(', ')}] at depth ${depth}` 
      });

      const sortedLeft = mergeSortRec(left, depth + 1);
      const sortedRight = mergeSortRec(right, depth + 1);

      const merged = merge(sortedLeft, sortedRight);
      stepsArray.push({ 
        left: sortedLeft, 
        right: sortedRight, 
        merged, 
        description: `Merging sorted left [${sortedLeft.join(', ')}] and right [${sortedRight.join(', ')}]` 
      });

      return merged;
    };

    mergeSortRec(arr);
    setSteps(stepsArray);
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleModeToggle = () => {
    if (mode === 'manual') {
      setMode('automatic');
    } else {
      setMode('manual');
      clearInterval(intervalId);
    }
  };

  const { left, right, merged, description } = steps.length > 0 ? steps[currentStep] : { left: [], right: [], merged: [], description: '' };

  return (
    <div>
      <h3>Merge Sort Visualization:</h3>
      <div className="array-container">
        <div className="array-section">
          <h4>Unsorted Array</h4>
          {array.map((value, index) => (
            <div key={index} className="array-item">
              {value}
            </div>
          ))}
        </div>
        <div className="array-section">
          <h4>Left</h4>
          {left.map((value, index) => (
            <div key={index} className="array-item">
              {value}
            </div>
          ))}
        </div>
        <div className="array-section">
          <h4>Right</h4>
          {right.map((value, index) => (
            <div key={index} className="array-item">
              {value}
            </div>
          ))}
        </div>
        <div className="array-section">
          <h4>Merged</h4>
          {merged.map((value, index) => (
            <div key={index} className="array-item merged">
              {value}
            </div>
          ))}
        </div>
      </div>
      <div className="step-description">
        <h4>Step Description:</h4>
        <p>{description}</p>
      </div>
      <div className="controls">
        <button onClick={handlePreviousStep} disabled={currentStep === 0 || mode === 'automatic'}>
          Previous
        </button>
        <button onClick={handleNextStep} disabled={currentStep >= steps.length - 1 || mode === 'automatic'}>
          Next
        </button>
        <button onClick={handleModeToggle}>
          {mode === 'manual' ? 'Switch to Automatic' : 'Switch to Manual'}
        </button>
      </div>
      <div className="algorithm-explanation">
        <h4>Merge Sort Algorithm Explanation</h4>
        <p>
          Merge Sort is a divide-and-conquer algorithm that divides the input array into two halves, calls itself for the two halves, and then merges the two sorted halves. The merge() function is used for merging two halves.
        </p>
        <ol>
          <li>Divide the array into two halves.</li>
          <li>Recursively sort the two halves.</li>
          <li>Merge the sorted halves to produce the sorted array.</li>
        </ol>
        <p>
          Merge Sort has a time complexity of O(n log n), which makes it highly efficient for larger datasets.
        </p>
      </div>
    </div>
  );
}

export default MergeSortVisualizer;
